import { useEventStore } from '@/state/stores/use-event-store';
import { Box, Typography, Stack, Button, Divider, Alert, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ContributorRow } from './contributor-row';
import { useMemo, useState } from 'react';
import { useInsufficientContributorCreditsInfo } from '@/hooks/use-insufficient-contributor-credits-info';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { Add, ForwardToInboxOutlined, AddShoppingCartOutlined, Diversity3Outlined } from '@mui/icons-material';
import useSendEventContributorsInvitations from '@/state/mutations/invitations/use-send-event-contributors-invitations';
import { useNotifications } from '@/hooks/use-notifications';
import InviteContributorModal from './invite-contributor.modal';
import CopyInviteLinkModal from './copy-invite-link.modal';
import { useCreateContributorMutation } from '@/state/mutations/contributors/use-create-contributor';
import { useCreateContributorRoleMutation } from '@/state/mutations/contributors/use-create-contributor-role';
import { SearchField } from '@/components/common/search-field';
import { getRoleSlotDescription } from '@/components/events/helpers/get-role-slot-description.helper';
import { getEntityName } from '@/components/events/helpers/get-entity-name.helper';
import type { CrewRole } from '@/types/dtos/contributors/response/contributor.dto';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto.ts';
import { useProductsStore } from '@/state/stores/use-products-store.ts';
import EventCatalogOrderCheckoutModal from '@/components/events/event-catalog/event-catalog-order-checkout.modal.tsx';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders.ts';
import { useCreateEventOrderMutation } from '@/state/mutations/orders/use-create-order.ts';
import { useUpdateOrderMutation } from '@/state/mutations/orders/use-update-order.ts';
import { ORDER_STATUS } from '@/types/dtos/orders/response/order.dto.ts';
import { getOriginEventId } from '@/helpers/event.helper';
import { getOrderLineItemProductId, mapOrderLineItemsToUpdatePayload } from '@/helpers/order-line-item.helper';
import usePatchContributorMutation from '@/state/mutations/contributors/use-patch-contributor';
import { usePatchContributorRoleMutation } from '@/state/mutations/contributors/use-patch-contributor-role';
import { usePatchContributorRoleSlotMutation } from '@/state/mutations/contributors/use-patch-contributor-role-slot';
import {
  buildStagedContributorsInvitationPayload,
  computeStagedInviteSkips,
  contributorHasInviteableStagedSelection,
} from '@/components/events/detail/tabs/contributors/staging-invite-build.helpers';
import {
  applyStagedForInvitePlan,
  computeStagedForInvitePatches,
} from '@/components/events/detail/tabs/contributors/staging-selection-cascade.helpers';
import { filterContributorsForFlowContext } from '@/components/events/detail/tabs/contributors/flow-context-contributors-filter';
import type { InvitePublicationUiContext } from '@/utils/contributor-invite-publication.utils';
import AddEditContributorModal from '@/components/events/detail/tabs/contributors/tab-actions/add-edit-contributor';

export default function ContributorsList({
  contributors,
  isAddAllowed = true,
  activateInlineAdding = false,
  flowContext,
}: {
  contributors: ContributorDto[];
  isAddAllowed?: boolean;
  activateInlineAdding?: boolean;
  flowContext?: FlowDto;
}) {
  const { t } = useTranslation();
  const notification = useNotifications();
  const event = useEventStore((s) => s.event);
  const eventForSlots = useEventStore((s) => s.eventOrganize);
  const eventFlows = useEventStore((s) => s.eventFlowsOrganize);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventFlowsPlan = useEventStore((s) => s.eventFlowsPlan);
  const eventSessionsPlan = useEventStore((s) => s.eventSessionsPlan);
  const eventFlowsOrganize = useEventStore((s) => s.eventFlowsOrganize);
  const eventSessionsOrganize = useEventStore((s) => s.eventSessionsOrganize);

  const invitePublicationCtx = useMemo((): InvitePublicationUiContext | undefined => {
    if (!eventPlan && !eventFlowsPlan?.length && !eventSessionsPlan?.length) return undefined;
    return {
      eventPlan,
      eventFlowsPlan,
      eventSessionsPlan,
      eventFlowsOrganize,
      eventSessionsOrganize,
    };
  }, [eventPlan, eventFlowsPlan, eventSessionsPlan, eventFlowsOrganize, eventSessionsOrganize]);

  const { mutateAsync: patchContributor } = usePatchContributorMutation();
  const { mutateAsync: patchContributorRole } = usePatchContributorRoleMutation();
  const { mutateAsync: patchContributorRoleSlot } = usePatchContributorRoleSlotMutation();

  const [isInviteContributorModalOpen, setIsInviteContributorModalOpen] = useState<boolean>(false);
  const [copyLinkContributor, setCopyLinkContributor] = useState<ContributorDto | undefined>();
  const [profileEditorContributor, setProfileEditorContributor] = useState<ContributorDto | undefined>();
  const [isProfileEditorOpen, setIsProfileEditorOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Track which contributor is being edited inline
  const [editingContributorId, setEditingContributorId] = useState<string | null>(null);

  // Derive showInlineInput from activateInlineAdding
  const [showInlineInputState, setShowInlineInputState] = useState(false);
  const showInlineInput = activateInlineAdding || showInlineInputState;
  const setShowInlineInput = (value: boolean) => {
    setShowInlineInputState(value);
  };

  const { mutateAsync: createContributorMutation } = useCreateContributorMutation();
  const { mutateAsync: createContributorRoleMutation } = useCreateContributorRoleMutation();
  const catalogProducts = useProductsStore((s) => s.products);
  const { data: orders } = useGetEventOrdersQuery(getOriginEventId(event));
  const { mutateAsync: createEventOrder } = useCreateEventOrderMutation();
  const { mutateAsync: updateOrder } = useUpdateOrderMutation();
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  const insufficientCreditsInfo = useInsufficientContributorCreditsInfo(event, contributors);

  // Function to add missing contributor credits and open checkout
  const handleAddMissingCredits = async () => {
    const originEventId = getOriginEventId(event);
    if (!insufficientCreditsInfo || !catalogProducts || !originEventId) return;

    const contributorProduct = catalogProducts.find((feature) => feature.key === PRODUCT_KEYS.EVENT_CONTRIBUTOR);
    if (!contributorProduct) return;

    try {
      const latestOrder = orders?.find(
        (order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING,
      );

      if (latestOrder?.status === ORDER_STATUS.DRAFT) {
        // Check if contributor feature already exists in the order
        const existingContributorLineItem = latestOrder.lineItems.find(
          (lineItem) => getOrderLineItemProductId(lineItem.product) === contributorProduct.id,
        );

        if (existingContributorLineItem) {
          const neededQuantity = insufficientCreditsInfo.neededCredits;

          const updatedOrder = {
            orderId: latestOrder.id,
            lineItems: mapOrderLineItemsToUpdatePayload(latestOrder.lineItems, {
              bumpProductId: contributorProduct.id,
              bumpQuantity: neededQuantity,
            }),
          };
          await updateOrder(updatedOrder);
        } else {
          // Add new contributor line item
          const updatedOrder = {
            orderId: latestOrder.id,
            lineItems: [
              ...mapOrderLineItemsToUpdatePayload(latestOrder.lineItems),
              {
                productId: contributorProduct.id,
                quantity: insufficientCreditsInfo.neededCredits,
              },
            ],
          };
          await updateOrder(updatedOrder);
        }
      } else {
        // Create new order with missing credits
        const newOrder = {
          eventId: originEventId,
          lineItems: [
            {
              productId: contributorProduct.id,
              quantity: insufficientCreditsInfo.neededCredits,
            },
          ],
        };
        await createEventOrder(newOrder);
      }

      // Open checkout modal
      setIsCheckoutModalOpen(true);
    } catch (error) {
      console.error('Failed to add missing credits:', error);
      notification.notifyError(t('event.detail.contributors.notifications.addCreditsError'));
    }
  };

  // Sort contributors by display name
  const sortedContributors = useMemo(() => {
    if (!contributors) return [];
    return [...contributors].sort((a, b) => {
      const nameA = a.profiles?.[0]?.displayName || '';
      const nameB = b.profiles?.[0]?.displayName || '';
      return nameA.localeCompare(nameB);
    });
  }, [contributors]);

  const flowSlotFilteredContributors = useMemo(
    () => filterContributorsForFlowContext(sortedContributors, flowContext, event?.multiFlow),
    [sortedContributors, flowContext, event?.multiFlow],
  );

  const contributorsWithStagedInvite = useMemo(
    () =>
      flowSlotFilteredContributors.filter((c) =>
        contributorHasInviteableStagedSelection(c, flowContext, invitePublicationCtx),
      ),
    [flowSlotFilteredContributors, flowContext, invitePublicationCtx],
  );

  const [inviteModalContributorIds, setInviteModalContributorIds] = useState<string[] | undefined>(undefined);

  const inviteModalSkips = useMemo(() => {
    if (!isInviteContributorModalOpen) return [];
    return computeStagedInviteSkips(flowSlotFilteredContributors, flowContext, {
      contributorIds: inviteModalContributorIds,
      invitePublicationCtx,
    });
  }, [
    isInviteContributorModalOpen,
    flowSlotFilteredContributors,
    flowContext,
    inviteModalContributorIds,
    invitePublicationCtx,
  ]);

  const inviteModalPayload = useMemo(() => {
    if (!isInviteContributorModalOpen) return [];
    const raw = buildStagedContributorsInvitationPayload(flowSlotFilteredContributors, flowContext, {
      contributorIds: inviteModalContributorIds,
      invitePublicationCtx,
    });
    const skipIds = new Set(inviteModalSkips.map((s) => s.contributorId));
    return raw.filter((row) => !skipIds.has(row.contributorId));
  }, [
    isInviteContributorModalOpen,
    flowSlotFilteredContributors,
    flowContext,
    inviteModalContributorIds,
    inviteModalSkips,
    invitePublicationCtx,
  ]);

  const contributorsToInviteCount = useMemo(() => {
    const list = flowSlotFilteredContributors;
    let n = 0;
    for (const row of inviteModalPayload) {
      const c = list.find((x) => x.id === row.contributorId);
      if (c && !c.invitedAt) n += 1;
    }
    return n;
  }, [inviteModalPayload, flowSlotFilteredContributors]);

  // Filter contributors by search term (enhanced with roles and slots)
  const filteredContributors = useMemo(() => {
    const contributorsToFilter = flowSlotFilteredContributors;
    if (!searchTerm.trim()) return contributorsToFilter;

    const searchLower = searchTerm.toLowerCase();
    return contributorsToFilter.filter((contributor) => {
      // Existing profile search
      const name = contributor.profiles?.[0]?.displayName || '';
      const title = contributor.profiles?.[0]?.title || '';
      const organization = contributor.profiles?.[0]?.organization || '';

      const profileMatch =
        name.toLowerCase().includes(searchLower) ||
        title.toLowerCase().includes(searchLower) ||
        organization.toLowerCase().includes(searchLower);

      if (profileMatch) return true;

      // Enhanced search through roles and slots
      return contributor.roles.some((role) => {
        // Search role labels (translated)
        const roleLabel = t(`common.roleLabels.${role.role}`).toLowerCase();
        if (roleLabel.includes(searchLower)) return true;

        // Search entity names (flow/session titles)
        if (role.entity) {
          const entityName = getEntityName({ entity: role.entity, entityType: role.entityType });
          if (entityName && entityName.toLowerCase().includes(searchLower)) return true;
        }

        // Search slot descriptions (timing info including flow names)
        return role.slots?.some((slot) => {
          const description = getRoleSlotDescription(slot, eventForSlots, eventFlows, flowContext);
          return description && description.toLowerCase().includes(searchLower);
        });
      });
    });
  }, [flowSlotFilteredContributors, searchTerm, t, eventForSlots, eventFlows, flowContext]);

  /** Match schedule/slot-assets empty style while the team is still small (≤3). */
  const showIntroPlaceholder = !searchTerm.trim() && flowSlotFilteredContributors.length <= 3;

  const { mutateAsync: sendEventContributorsInvitationsMutation, isPending: sendEventContributorsInvitationsPending } =
    useSendEventContributorsInvitations();

  const handleAddNewContributor = () => {
    // Cancel any current inline editing
    setEditingContributorId(null);
    setShowInlineInput(true);
  };

  const handleOpenEventProfileEditor = (contributor: ContributorDto) => {
    setProfileEditorContributor(contributor);
    setIsProfileEditorOpen(true);
  };

  const handleStartInlineEdit = (contributorId: string) => {
    // Cancel new contributor creation and any other inline editing
    setShowInlineInput(false);
    setEditingContributorId(contributorId);
  };

  const handleStopInlineEdit = () => {
    setEditingContributorId(null);
  };

  const handleSaveNewContributor = async (contributorData: {
    displayName: string;
    organization: string;
    title: string;
    email: string;
    phoneNumber: string;
    crewRole?: string;
  }) => {
    if (!event?.id) return;

    try {
      // Create the contributor first
      const createdContributor = await createContributorMutation({
        event: event.id,
        profiles: [
          {
            displayName: contributorData.displayName,
            organization: contributorData.organization || undefined,
            title: contributorData.title || undefined,
            editor: 'Event',
          },
        ],
        email: contributorData.email || undefined,
        phoneNumber: contributorData.phoneNumber || undefined,
        roles: [],
      });

      // If a crew role was selected, add it to the contributor
      if (contributorData.crewRole && createdContributor?.id) {
        await createContributorRoleMutation({
          contributorId: createdContributor.id,
          role: {
            role: contributorData.crewRole as CrewRole,
            entity: event.id,
            entityType: 'Event',
          },
        });
      }

      notification.notifySuccess(t('event.detail.contributors.notifications.addContributorSuccess'));

      setShowInlineInput(false);
    } catch (error) {
      console.error('Failed to create contributor:', error);
      notification.notifyError(t('event.detail.contributors.notifications.addContributorError'));
    }
  };

  const handleContributionSelected = async ({
    isSelected,
    contributorId,
    roleId,
    slotId,
  }: {
    isSelected: boolean;
    contributorId: string;
    roleId?: string;
    slotId?: string;
  }) => {
    const c = contributors?.find((x) => x.id === contributorId);
    if (!c) return;
    const plan = computeStagedForInvitePatches(c, { isSelected, contributorId, roleId, slotId });
    try {
      await applyStagedForInvitePlan(plan, {
        patchContributor,
        patchContributorRole,
        patchContributorRoleSlot,
      });
    } catch {
      notification.notifyError(t('event.detail.contributors.notifications.updateContributorFailed'));
    }
  };

  const openInviteModal = (contributorIds?: string[]) => {
    setInviteModalContributorIds(contributorIds);
    setIsInviteContributorModalOpen(true);
  };

  const handleInviteContributor = () => {
    openInviteModal(undefined);
  };

  const handleCloseInviteModal = () => {
    setIsInviteContributorModalOpen(false);
    setInviteModalContributorIds(undefined);
  };

  const sendEventContributorInvitations = async () => {
    if (!event?.id) return;
    if (inviteModalPayload.length === 0) {
      notification.notifyError(t('event.detail.contributors.notifications.sendInvitationsFailed'));
      return;
    }

    try {
      await sendEventContributorsInvitationsMutation({
        eventId: event.id,
        contributors: inviteModalPayload,
      });

      notification.notifySuccess(t('event.detail.contributors.notifications.sendInvitationsSuccess'));
      handleCloseInviteModal();
    } catch {
      notification.notifyError(t('event.detail.contributors.notifications.sendInvitationsFailed'));
    }
  };

  const inviteModalTitle = useMemo(() => {
    if (inviteModalPayload.length === 1) {
      const c = flowSlotFilteredContributors.find((x) => x.id === inviteModalPayload[0].contributorId);
      return t('contributor.inviteContributorModal.title', {
        name: c?.profiles?.[0]?.displayName ?? '',
      });
    }
    return t('contributor.inviteContributorsModal.title', { count: inviteModalPayload.length });
  }, [inviteModalPayload, flowSlotFilteredContributors, t]);

  const inviteModalSkippedRows = useMemo(
    () =>
      inviteModalSkips.map((s) => ({
        displayName: s.displayName,
        reasons: s.reasons,
      })),
    [inviteModalSkips],
  );

  return (
    <Stack spacing={1}>
      {/* Insufficient credits alert */}
      {insufficientCreditsInfo && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={
            <IconButton color="secondary" size="small" onClick={handleAddMissingCredits} sx={{ p: 0.5 }}>
              <AddShoppingCartOutlined />
            </IconButton>
          }
        >
          {t('event.detail.contributors.insufficientCredits', {
            needed: insufficientCreditsInfo.neededCredits,
            notInvited: insufficientCreditsInfo.notInvitedCount,
          })}
        </Alert>
      )}

      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          position: 'sticky',
          // Event tab: pin below main nav + measured FixedNavigationBar (see fixed-top-bar ResizeObserver).
          // Flow / dialog: list lives in `overflow-y: auto`; stick to the scroll container top.
          top: flowContext != null ? 0 : 'calc(var(--navbar-height) + var(--fixed-navigation-bar-height))',
          zIndex: theme.zIndex.appBar - 1,
          alignItems: 'center',
          py: 0,
          flexWrap: 'wrap',
          rowGap: 1,
          bgcolor: theme.palette.background.default,
          ...(flowContext != null
            ? {
                mx: -1,
                px: 1,
              }
            : {}),
        })}
      >
        {/* Buttons - hidden when search is expanded */}
        {!isSearchExpanded && (
          <>
            <Button
              color="primary"
              variant="contained"
              size="small"
              data-testid="invite-contributors-button"
              disabled={!contributorsWithStagedInvite.length || sendEventContributorsInvitationsPending}
              onClick={handleInviteContributor}
              startIcon={<ForwardToInboxOutlined />}
              sx={{ flexShrink: 0 }}
            >
              {t('common.invite')}
            </Button>
            {isAddAllowed && (
              <>
                <Divider orientation="vertical" flexItem />
                <Button
                  color="info"
                  variant="contained"
                  size="small"
                  data-testid="add-contributor-button"
                  onClick={handleAddNewContributor}
                  startIcon={<Add />}
                  sx={{ flexShrink: 0 }}
                >
                  {t('common.new')}
                </Button>
              </>
            )}
          </>
        )}

        {/* Search field - positioned on the right */}
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            flex: isSearchExpanded ? 1 : 'none',
            justifyContent: 'flex-end',
            minWidth: isSearchExpanded ? '100%' : 'auto',
          }}
        >
          <SearchField value={searchTerm} onChange={setSearchTerm} width={300} onExpandChange={setIsSearchExpanded} />
        </Box>
      </Stack>

      <AddEditContributorModal
        contributor={profileEditorContributor}
        open={isProfileEditorOpen}
        setOpen={setIsProfileEditorOpen}
        eventSlotId={flowContext?.slot?.id}
        profileOnly
      />
      <EventCatalogOrderCheckoutModal
        open={isCheckoutModalOpen}
        event={event}
        onModalClose={() => setIsCheckoutModalOpen(false)}
      />
      <InviteContributorModal
        open={isInviteContributorModalOpen}
        onModalClose={handleCloseInviteModal}
        onConfirm={sendEventContributorInvitations}
        contributorsToInviteCount={contributorsToInviteCount}
        skippedContributors={inviteModalSkippedRows}
        confirmDisabled={inviteModalPayload.length === 0}
        invitePayload={inviteModalPayload}
        contributors={flowSlotFilteredContributors}
        flowContext={flowContext}
        title={inviteModalTitle}
        text={
          inviteModalPayload.length === 1
            ? t('contributor.inviteContributorModal.text')
            : t('contributor.inviteContributorsModal.text')
        }
      />
      {copyLinkContributor && event?.id && (
        <CopyInviteLinkModal
          open={!!copyLinkContributor}
          onClose={() => setCopyLinkContributor(undefined)}
          contributor={copyLinkContributor}
          eventId={event.id}
          contributors={contributors}
          flowContext={flowContext}
        />
      )}
      {showInlineInput && (
        <ContributorRow
          contributor={
            {
              id: 'new',
              profiles: [],
              email: '',
              phoneNumber: '',
              invitedAt: undefined,
              acceptedAt: undefined,
              roles: [],
            } as ContributorDto
          }
          isNewContributor={true}
          onSaveNew={handleSaveNewContributor}
          onCancelNew={() => setShowInlineInput(false)}
          onInviteContributor={() => {}}
          onCopyInviteLink={() => {}}
          onContributionSelected={() => {}}
          isInlineEditing={true}
          onStartInlineEdit={() => {}}
          onStopInlineEdit={() => setShowInlineInput(false)}
          flowContext={flowContext}
          invitePublicationCtx={invitePublicationCtx}
        />
      )}
      <Box>
        {filteredContributors.length > 0 ? (
          filteredContributors.map((contributor, i) => (
            <ContributorRow
              key={`contributor-row-${contributor.id}-${i}`}
              contributor={contributor}
              onOpenEventProfileEditor={() => handleOpenEventProfileEditor(contributor)}
              onContributionSelected={handleContributionSelected}
              onInviteContributor={() => openInviteModal([contributor.id])}
              onCopyInviteLink={() => setCopyLinkContributor(contributor)}
              isInlineEditing={editingContributorId === contributor.id}
              onStartInlineEdit={() => handleStartInlineEdit(contributor.id)}
              onStopInlineEdit={handleStopInlineEdit}
              searchTerm={searchTerm}
              flowContext={flowContext}
              invitePublicationCtx={invitePublicationCtx}
            />
          ))
        ) : searchTerm.trim() ? (
          <Typography variant="body2" color="text.secondary">
            {t('common.noResultsFound')}
          </Typography>
        ) : null}
        {showIntroPlaceholder ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              px: 3,
              py: 3,
              textAlign: 'center',
            }}
          >
            <Diversity3Outlined sx={{ fontSize: 40, color: 'info.main', mb: 0.5 }} />
            <Typography variant="subtitle1" fontWeight={600} color="text.primary">
              {t('event.detail.contributors.noContributorsTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              {t('event.detail.contributors.noContributorsHint')}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Stack>
  );
}
